<template>
  <div class="oidc-settings-overlay" @click.self="onBackdropClick">
    <div class="settings-container">
      <!-- Logo Section -->
      <div class="logo-section">
        <img 
          :src="splashLogoUrl" 
          alt="MiniFlow Logo" 
          class="splash-logo"
          @error="onLogoError"
        />
        <h1 class="app-title">MiniFlow</h1>
        <p class="app-subtitle">OIDC Configuration</p>
      </div>

      <!-- Settings Form -->
      <div class="form-section">
        <div class="settings-card">
          <h2>Configure OIDC Provider</h2>
          <p class="settings-description">Enter your OIDC provider settings to authenticate users</p>
          
          <form @submit.prevent="handleSave" class="settings-form">
            <!-- OIDC Server URL Field -->
            <div class="form-group">
              <label for="oidcServerUrl">OIDC Server URL</label>
              <input
                id="oidcServerUrl"
                v-model="form.oidc_server_url"
                type="url"
                placeholder="https://auth.example.com"
                required
                autocomplete="off"
                :disabled="isLoading"
              />
              <span class="field-hint">The base URL of your OIDC provider</span>
            </div>

            <!-- OIDC Issuer Field -->
            <div class="form-group">
              <label for="oidcIssuer">Issuer</label>
              <input
                id="oidcIssuer"
                v-model="form.oidc_issuer"
                type="text"
                placeholder="https://auth.example.com"
                required
                autocomplete="off"
                :disabled="isLoading"
              />
              <span class="field-hint">The issuer identifier (iss claim)</span>
            </div>

            <!-- OIDC Audience Field -->
            <div class="form-group">
              <label for="oidcAudience">Audience</label>
              <input
                id="oidcAudience"
                v-model="form.oidc_audience"
                type="text"
                placeholder="miniflow-api"
                required
                autocomplete="off"
                :disabled="isLoading"
              />
              <span class="field-hint">The intended audience (aud claim)</span>
            </div>

            <!-- JWKS URL Field -->
            <div class="form-group">
              <label for="jwksUrl">JWKS URL (optional)</label>
              <input
                id="jwksUrl"
                v-model="form.jwks_url"
                type="url"
                placeholder="https://auth.example.com/.well-known/jwks.json"
                autocomplete="off"
                :disabled="isLoading"
              />
              <span class="field-hint">URL to the JSON Web Key Set (auto-discovered if not provided)</span>
            </div>

            <!-- Error Message -->
            <div v-if="saveError" class="error-message">
              <span class="error-icon">⚠️</span>
              {{ saveError }}
            </div>

            <!-- Success Message -->
            <div v-if="saveSuccess" class="success-message">
              <span class="success-icon">✅</span>
              {{ saveSuccess }}
            </div>

            <!-- Loading State -->
            <button
              type="submit"
              class="save-button"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="loading-spinner"></span>
              <span>{{ isLoading ? 'Saving...' : 'Save & Continue' }}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const emit = defineEmits<{
  save: [settings: OidcSettings]
}>()

interface OidcSettings {
  oidc_server_url: string
  oidc_issuer: string
  oidc_audience: string
  jwks_url?: string
}

interface Props {
  initialSettings?: OidcSettings
  onSaveSuccess?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  initialSettings: () => ({
    oidc_server_url: '',
    oidc_issuer: '',
    oidc_audience: '',
    jwks_url: ''
  }),
  onSaveSuccess: () => {}
})

// Form state
const form = ref<OidcSettings>({ ...props.initialSettings })
const isLoading = ref(false)
const saveError = ref<string | null>(null)
const saveSuccess = ref<string | null>(null)

// Splash logo URL
const splashLogoUrl = ref('/logo.png')

function onLogoError() {
  splashLogoUrl.value = ''
}

function onBackdropClick() {
  // Optionally close on backdrop click
  // emit('close')
}

async function handleSave() {
  isLoading.value = true
  saveError.value = null
  saveSuccess.value = null

  try {
    // Validate required fields
    if (!form.value.oidc_server_url || !form.value.oidc_issuer || !form.value.oidc_audience) {
      saveError.value = 'All required fields must be filled'
      return
    }

    // Validate URL formats
    try {
      new URL(form.value.oidc_server_url)
    } catch {
      saveError.value = 'Invalid OIDC Server URL format'
      return
    }

    try {
      if (form.value.jwks_url) {
        new URL(form.value.jwks_url)
      }
    } catch {
      saveError.value = 'Invalid JWKS URL format'
      return
    }

    // Validate issuer format
    try {
      new URL(form.value.oidc_issuer)
    } catch {
      saveError.value = 'Invalid Issuer URL format'
      return
    }

    // Emit the settings to parent
    emit('save', { ...form.value })
    
    saveSuccess.value = 'Settings saved successfully!'
    
    if (props.onSaveSuccess) {
      props.onSaveSuccess()
    }
  } catch (err) {
    saveError.value = err instanceof Error ? err.message : 'Failed to save settings'
  } finally {
    isLoading.value = false
  }
}

// Load existing settings on mount
onMounted(async () => {
  if (props.initialSettings) {
    form.value = { ...props.initialSettings }
  }
})
</script>

<style scoped>
.oidc-settings-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  backdrop-filter: blur(10px);
}

.settings-container {
  display: flex;
  flex-direction: row;
  background: #1a1a2e;
  border-radius: 16px;
  overflow: hidden;
  max-width: 900px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.logo-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: linear-gradient(135deg, #16213e 0%, #0f3460 100%);
}

.splash-logo {
  width: 120px;
  height: 120px;
  object-fit: contain;
  margin-bottom: 20px;
  border-radius: 20px;
}

.app-title {
  color: #e94560;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.app-subtitle {
  color: #a0a0b0;
  font-size: 1rem;
  margin-top: 8px;
}

.form-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.settings-card {
  width: 100%;
  max-width: 420px;
}

.settings-card h2 {
  color: #ffffff;
  font-size: 1.75rem;
  margin: 0 0 8px 0;
}

.settings-description {
  color: #a0a0b0;
  font-size: 0.9rem;
  margin: 0 0 24px 0;
}

.settings-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #e0e0e0;
  font-size: 0.875rem;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  background: #16213e;
  border: 1px solid #2a2a4a;
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #e94560;
  box-shadow: 0 0 0 3px rgba(233, 69, 96, 0.2);
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.field-hint {
  display: block;
  color: #707080;
  font-size: 0.75rem;
  margin-top: 4px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(233, 69, 96, 0.15);
  border: 1px solid rgba(233, 69, 96, 0.3);
  border-radius: 8px;
  color: #ff6b8a;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(46, 204, 113, 0.15);
  border: 1px solid rgba(46, 204, 113, 0.3);
  border-radius: 8px;
  color: #2ecc71;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.save-button {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #e94560 0%, #c23152 100%);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.save-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(233, 69, 96, 0.4);
}

.save-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .settings-container {
    flex-direction: column;
  }

  .logo-section {
    padding: 24px;
  }

  .splash-logo {
    width: 80px;
    height: 80px;
  }

  .app-title {
    font-size: 1.5rem;
  }

  .form-section {
    padding: 24px;
  }
}
</style>
